import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Welcome from '../components/onboarding/Welcome';
import OracleQuestion from '../components/onboarding/OracleQuestion';
import PhilosophicalCompass from '../components/onboarding/PhilosophicalCompass';
import Personalization from '../components/onboarding/Personalization';
import AuthModal from '../components/auth/AuthModal';
import { useAuth } from '../services/AuthContext';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [userPreferences, setUserPreferences] = useState({});
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('signup');
  const { currentUser, updateUserProfile } = useAuth();

  useEffect(() => {
    if (currentUser) {
      if (currentUser.preferences) {
        setUserPreferences(prev => ({
          ...prev,
          ...currentUser.preferences
        }));
      }
    }
  }, [currentUser]);

  const handleContinueFromWelcome = () => setStep(2);

  const handleContinueFromOracle = (reason) => {
    setUserPreferences(prev => ({ ...prev, reason }));
    setStep(3);
  };

  const handleContinueFromCompass = (preferences) => {
    setUserPreferences(prev => ({ ...prev, ...preferences }));
    setStep(4);
  };

  const handleCompletionFromPersonalization = () => {
    if (currentUser) {
      updateUserProfile({ preferences: userPreferences });
    } else {
      setAuthModalOpen(true);
      localStorage.setItem('tempUserPreferences', JSON.stringify(userPreferences));
    }
    navigate('/journal');
  };

  const handleSkipOnboarding = () => {
    const defaultPreferences = {
      reason: 'general interest',
      learningStyle: 'text',
      interests: ['ethics'],
      lifeGoal: 'knowledge',
      skippedOnboarding: true
    };
    setUserPreferences(defaultPreferences);
    if (currentUser) {
      updateUserProfile({ preferences: defaultPreferences });
    }
    navigate('/journal');
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'radial-gradient(ellipse at 50% 20%, #162a4a 0%, #0a1628 60%, #060e1a 100%)' }}>
      {/* Header */}
      <header className="py-6 px-4 border-b border-oliveGold/10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="font-serif text-2xl font-bold text-white">The <span className="text-oliveGold">Oikosystem</span></div>
          <div className="flex items-center gap-4">
            {/* Progress dots */}
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    s === step ? 'bg-oliveGold w-6' : s < step ? 'bg-oliveGold/60' : 'bg-white/20'
                  }`}
                />
              ))}
            </div>
            <button
              onClick={handleSkipOnboarding}
              className="text-sm text-white/40 hover:text-white/70 transition-colors"
            >
              Skip
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-grow flex items-center justify-center p-4">
        <div className="w-full max-w-4xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {step === 1 && <Welcome onContinue={handleContinueFromWelcome} />}
              {step === 2 && <OracleQuestion onContinue={handleContinueFromOracle} />}
              {step === 3 && <PhilosophicalCompass onContinue={handleContinueFromCompass} />}
              {step === 4 && <Personalization userPreferences={userPreferences} onContinue={handleCompletionFromPersonalization} />}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 px-4 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="text-sm text-white/20">
            &copy; {new Date().getFullYear()} Ancient Greece Revisited
          </div>
          <div className="text-sm">
            <Link to="/privacy" className="text-white/30 hover:text-white/50 transition-colors">Privacy</Link>
            <span className="mx-2 text-white/10">|</span>
            <Link to="/terms" className="text-white/30 hover:text-white/50 transition-colors">Terms</Link>
          </div>
        </div>
      </footer>

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMode={authMode}
      />
    </div>
  );
};

export default OnboardingPage;
